import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, throwError } from 'rxjs'
import { catchError } from 'rxjs/operators'
import { environment } from '../../../environments/environment'
import {
  LegacyCalculationResult,
  LegacyActiveContext,
  LegacyActiveContextUpdate,
  LegacyClient,
  LegacyCompareResult,
  LegacyCreateClientRequest,
  LegacyCreateGrossLossSetRequest,
  LegacyCreateLayerRequest,
  LegacyCreateProgramRequest,
  LegacyCreateStructureRequest,
  LegacyCreateYearRequest,
  LegacyDataset,
  LegacyGrossLossSet,
  LegacyGrossLossSetSummary,
  LegacyGrossLossSimulationResult,
  LegacyLayer,
  LegacyLayerSummary,
  LegacyMetricsSummary,
  LegacyProgram,
  LegacyProgramSummary,
  LegacyReservingResult,
  LegacySensitivityRequest,
  LegacySensitivityResult,
  LegacyStructure,
  LegacyStructureMetricsSummary,
  LegacyStructureSummary,
  LegacyUpdateGrossLossSetRequest,
  LegacyUpdateLayerRequest,
  LegacyUpdateProgramRequest,
  LegacyUpdateYearRequest,
  LegacyYear,
  LegacyYearProjection,
  LegacyYearSummary,
  LegacyCedantProjectionResult,
} from '../models/legacy-api.model'

@Injectable({
  providedIn: 'root',
})
export class LegacyApiService {
  private readonly base = environment.legacyApi.base

  constructor(private http: HttpClient) {}

  listClients(): Observable<LegacyClient[]> {
    return this.request(this.http.get<LegacyClient[]>(`${this.base}/clients/`), 'load clients')
  }

  getAllowedRoles(): Observable<string[]> {
    return this.request(
      this.http.get<string[]>(`${this.base}/auth/allowed-roles`),
      'load allowed roles',
    )
  }

  getActiveContext(): Observable<LegacyActiveContext> {
    return this.request(
      this.http.get<LegacyActiveContext>(`${this.base}/user/active-context`),
      'load active context',
    )
  }

  saveActiveContext(body: LegacyActiveContextUpdate): Observable<LegacyActiveContext> {
    return this.request(
      this.http.put<LegacyActiveContext>(`${this.base}/user/active-context`, body),
      'save active context',
    )
  }

  createClient(body: LegacyCreateClientRequest): Observable<LegacyClient> {
    return this.request(
      this.http.post<LegacyClient>(`${this.base}/clients/`, body),
      'create client',
    )
  }

  getClient(clientId: string): Observable<LegacyClient> {
    return this.request(
      this.http.get<LegacyClient>(`${this.base}/clients/${clientId}`),
      'load client',
    )
  }

  updateClient(
    clientId: string,
    body: Partial<LegacyCreateClientRequest>,
  ): Observable<LegacyClient> {
    return this.request(
      this.http.patch<LegacyClient>(`${this.base}/clients/${clientId}`, body),
      'update client',
    )
  }

  deleteClient(clientId: string): Observable<void> {
    return this.request(this.http.delete<void>(`${this.base}/clients/${clientId}`), 'delete client')
  }

  listYears(clientId: string): Observable<LegacyYearSummary[]> {
    return this.request(
      this.http.get<LegacyYearSummary[]>(`${this.base}/clients/${clientId}/years`),
      'load years',
    )
  }

  createYear(clientId: string, body: LegacyCreateYearRequest): Observable<LegacyYear> {
    return this.request(
      this.http.post<LegacyYear>(`${this.base}/clients/${clientId}/years`, body),
      'create year',
    )
  }

  getYear(yearId: string): Observable<LegacyYear> {
    return this.request(this.http.get<LegacyYear>(`${this.base}/years/${yearId}`), 'load year')
  }

  updateYear(yearId: string, body: LegacyUpdateYearRequest): Observable<LegacyYear> {
    return this.request(
      this.http.patch<LegacyYear>(`${this.base}/years/${yearId}`, body),
      'update year',
    )
  }

  deleteYear(yearId: string): Observable<void> {
    return this.request(this.http.delete<void>(`${this.base}/years/${yearId}`), 'delete year')
  }

  listPrograms(yearId: string): Observable<LegacyProgramSummary[]> {
    return this.request(
      this.http.get<LegacyProgramSummary[]>(`${this.base}/years/${yearId}/programs`),
      'load programs',
    )
  }

  createProgram(yearId: string, body: LegacyCreateProgramRequest): Observable<LegacyProgram> {
    return this.request(
      this.http.post<LegacyProgram>(`${this.base}/years/${yearId}/programs`, body),
      'create program',
    )
  }

  getProgram(programId: string): Observable<LegacyProgram> {
    return this.request(
      this.http.get<LegacyProgram>(`${this.base}/programs/${programId}`),
      'load program',
    )
  }

  updateProgram(programId: string, body: LegacyUpdateProgramRequest): Observable<LegacyProgram> {
    return this.request(
      this.http.patch<LegacyProgram>(`${this.base}/programs/${programId}`, body),
      'update program',
    )
  }

  deleteProgram(programId: string): Observable<void> {
    return this.request(
      this.http.delete<void>(`${this.base}/programs/${programId}`),
      'delete program',
    )
  }

  listStructures(programId: string): Observable<LegacyStructureSummary[]> {
    return this.request(
      this.http.get<LegacyStructureSummary[]>(`${this.base}/programs/${programId}/structures`),
      'load structures',
    )
  }

  createStructure(
    programId: string,
    body: LegacyCreateStructureRequest,
  ): Observable<LegacyStructure> {
    return this.request(
      this.http.post<LegacyStructure>(`${this.base}/programs/${programId}/structures`, body),
      'create structure',
    )
  }

  getStructure(structureId: string): Observable<LegacyStructure> {
    return this.request(
      this.http.get<LegacyStructure>(`${this.base}/structures/${structureId}`),
      'load structure',
    )
  }

  updateStructure(
    structureId: string,
    body: Partial<LegacyCreateStructureRequest>,
  ): Observable<LegacyStructure> {
    return this.request(
      this.http.patch<LegacyStructure>(`${this.base}/structures/${structureId}`, body),
      'update structure',
    )
  }

  deleteStructure(structureId: string): Observable<void> {
    return this.request(
      this.http.delete<void>(`${this.base}/structures/${structureId}`),
      'delete structure',
    )
  }

  getStructureMetrics(structureId: string): Observable<LegacyStructureMetricsSummary> {
    return this.request(
      this.http.get<LegacyStructureMetricsSummary>(
        `${this.base}/structures/${structureId}/metrics`,
      ),
      'load structure metrics',
    )
  }

  listGrossLossSets(programId: string): Observable<LegacyGrossLossSetSummary[]> {
    return this.request(
      this.http.get<LegacyGrossLossSetSummary[]>(
        `${this.base}/programs/${programId}/gross-loss-sets`,
      ),
      'load gross loss sets',
    )
  }

  listDatasets(programId: string): Observable<LegacyDataset[]> {
    return this.request(
      this.http.get<LegacyDataset[]>(`${this.base}/programs/${programId}/datasets`),
      'load datasets',
    )
  }

  listReservingResults(datasetId: string): Observable<LegacyReservingResult[]> {
    return this.request(
      this.http.get<LegacyReservingResult[]>(
        `${this.base}/datasets/${datasetId}/reserving-results`,
      ),
      'load reserving results',
    )
  }

  getReservingResult(reservingResultId: string): Observable<LegacyReservingResult> {
    return this.request(
      this.http.get<LegacyReservingResult>(`${this.base}/reserving-results/${reservingResultId}`),
      'load reserving result',
    )
  }

  createGrossLossSet(
    programId: string,
    body: LegacyCreateGrossLossSetRequest,
  ): Observable<LegacyGrossLossSet> {
    return this.request(
      this.http.post<LegacyGrossLossSet>(
        `${this.base}/programs/${programId}/gross-loss-sets`,
        body,
      ),
      'create gross loss set',
    )
  }

  getGrossLossSet(grossLossSetId: string): Observable<LegacyGrossLossSet> {
    return this.request(
      this.http.get<LegacyGrossLossSet>(`${this.base}/gross-loss-sets/${grossLossSetId}`),
      'load gross loss set',
    )
  }

  updateGrossLossSet(
    grossLossSetId: string,
    body: LegacyUpdateGrossLossSetRequest,
  ): Observable<LegacyGrossLossSet> {
    return this.request(
      this.http.patch<LegacyGrossLossSet>(`${this.base}/gross-loss-sets/${grossLossSetId}`, body),
      'update gross loss set',
    )
  }

  deleteGrossLossSet(grossLossSetId: string): Observable<void> {
    return this.request(
      this.http.delete<void>(`${this.base}/gross-loss-sets/${grossLossSetId}`),
      'delete gross loss set',
    )
  }

  uploadGrossLossSet(
    programId: string,
    file: File,
    name: string,
    bookedReserve: number,
  ): Observable<LegacyGrossLossSet> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('name', name)
    formData.append('booked_reserve', bookedReserve.toString())
    formData.append('program_id', programId)
    return this.request(
      this.http.post<LegacyGrossLossSet>(`${this.base}/gross-loss-sets/upload`, formData),
      'upload gross loss set',
    )
  }

  simulateGrossLossSet(
    grossLossSetId: string,
    body?: { n_simulations?: number | null; seed?: number | null },
  ): Observable<LegacyGrossLossSimulationResult> {
    return this.request(
      this.http.post<LegacyGrossLossSimulationResult>(
        `${this.base}/gross-loss-sets/${grossLossSetId}/simulate`,
        body ?? {},
      ),
      'simulate gross loss set',
    )
  }

  getGrossLossSetSimulation(grossLossSetId: string): Observable<LegacyGrossLossSimulationResult> {
    return this.request(
      this.http.get<LegacyGrossLossSimulationResult>(
        `${this.base}/gross-loss-sets/${grossLossSetId}/simulation`,
      ),
      'load gross loss simulation',
    )
  }

  listLayers(structureId: string): Observable<LegacyLayerSummary[]> {
    return this.request(
      this.http.get<LegacyLayerSummary[]>(`${this.base}/structures/${structureId}/layers`),
      'load layers',
    )
  }

  createLayer(structureId: string, body: LegacyCreateLayerRequest): Observable<LegacyLayer> {
    return this.request(
      this.http.post<LegacyLayer>(`${this.base}/structures/${structureId}/layers`, body),
      'create layer',
    )
  }

  getLayer(layerId: string): Observable<LegacyLayer> {
    return this.request(this.http.get<LegacyLayer>(`${this.base}/layers/${layerId}`), 'load layer')
  }

  updateLayer(layerId: string, body: LegacyUpdateLayerRequest): Observable<LegacyLayer> {
    return this.request(
      this.http.patch<LegacyLayer>(`${this.base}/layers/${layerId}`, body),
      'update layer',
    )
  }

  deleteLayer(layerId: string): Observable<void> {
    return this.request(this.http.delete<void>(`${this.base}/layers/${layerId}`), 'delete layer')
  }

  calculateLayer(layerId: string): Observable<LegacyCalculationResult> {
    return this.request(
      this.http.post<LegacyCalculationResult>(`${this.base}/layers/${layerId}/calculate`, {}),
      'calculate layer',
    )
  }

  getLayerMetrics(layerId: string): Observable<LegacyMetricsSummary> {
    return this.request(
      this.http.get<LegacyMetricsSummary>(`${this.base}/layers/${layerId}/metrics`),
      'load layer metrics',
    )
  }

  getLayerResults(layerId: string): Observable<LegacyCalculationResult> {
    return this.request(
      this.http.get<LegacyCalculationResult>(`${this.base}/layers/${layerId}/results`),
      'load layer results',
    )
  }

  getLayerProjections(layerId: string): Observable<LegacyYearProjection[]> {
    return this.request(
      this.http.get<LegacyYearProjection[]>(`${this.base}/layers/${layerId}/projections`),
      'load layer projections',
    )
  }

  getCedantProjection(layerId: string): Observable<LegacyCedantProjectionResult> {
    return this.request(
      this.http.get<LegacyCedantProjectionResult>(
        `${this.base}/layers/${layerId}/cedant-projection`,
      ),
      'load cedant projection',
    )
  }

  compareLayers(layerIds: string[]): Observable<LegacyCompareResult> {
    return this.request(
      this.http.post<LegacyCompareResult>(`${this.base}/layers/compare`, { layer_ids: layerIds }),
      'compare layers',
    )
  }

  runSensitivity(
    layerId: string,
    body: LegacySensitivityRequest,
  ): Observable<LegacySensitivityResult> {
    return this.request(
      this.http.post<LegacySensitivityResult>(`${this.base}/layers/${layerId}/sensitivity`, body),
      'run sensitivity analysis',
    )
  }

  postStructureThumbnail(structureId: string, fd: FormData): Observable<void> {
    return this.request(
      this.http.post<void>(`${this.base}/structures/${structureId}/thumbnail`, fd),
      'post structure thumbnail',
    )
  }

  getStructureThumbnailBlob(structureId: string): Observable<Blob> {
    return this.request(
      this.http.get(`${this.base}/structures/${structureId}/thumbnail`, { responseType: 'blob' }),
      'load structure thumbnail',
    )
  }

  private request<T>(request: Observable<T>, context: string): Observable<T> {
    return request.pipe(
      catchError((error: HttpErrorResponse) => throwError(() => this.toError(error, context))),
    )
  }

  private toError(error: HttpErrorResponse, context: string): Error {
    const detail = error.error?.detail
    if (Array.isArray(detail)) {
      const message = detail
        .map((item) => item?.msg || item?.message || JSON.stringify(item))
        .join(', ')
      return new Error(message || `Unable to ${context}.`)
    }

    if (typeof detail === 'string' && detail.length) {
      return new Error(detail)
    }

    return new Error(error.message || `Unable to ${context}.`)
  }
}
